///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The geometry answer inside the cutout: the plane the aftershocks define
/// (least-squares fit) and the two nodal planes of the USGS moment tensor,
/// drawn as translucent sheets through the hypocenters. When the fitted
/// plane agrees with one nodal plane, that one is highlighted: three
/// independent lines of evidence agreeing is how the actual fault plane is
/// chosen. Built in true km, then the depth axis is stretched like
/// everything else in the frame.
///

import { TextNode } from "@/earthbrowser/entities/TextNode/TextNode"
import { type Font } from "@/earthbrowser/libs/opentype"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color, rgba } from "@stovokor/math/color"
import { type cube } from "@stovokor/math/cube"
import { matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { ruptureLengthKm, type CutoutLayout } from "./cutoutLayout"
import { normalFromStrikeDip, planeAngleDeg, strikeDifferenceDeg, type PlaneFit } from "./sequenceStats"
import { type MomentTensor, type NodalPlane } from "./UsgsDetail"

/** Nodal plane within this many degrees of the fitted plane counts as a match */
const cMatchAngleDeg = 25

const cFitColor = rgba(0.62, 0.90, 1.00, 0.22)
const cMatchColor = rgba(1.00, 0.76, 0.32, 0.26)
const cOtherColor = rgba(0.72, 0.56, 1.00, 0.10)
const cNeutralColor = rgba(0.80, 0.72, 1.00, 0.18)

type Vec = { x: number; y: number; z: number }

export interface FaultPlaneSummary {
	/**
	 * Fitted aftershock plane, when the cloud is planar enough. With
	 * dipResolved false only the strike is trustworthy (catalog depths were
	 * fixed) and no sheet is drawn for it
	 */
	fit?: { strikeDeg: number; dipDeg: number; dipResolved: boolean; trendResolved: boolean; n: number; flatness: number }
	nodalPlanes?: { np1: NodalPlane; np2: NodalPlane; source: string }
	/**
	 * 1 or 2 when a nodal plane matches the fit (full plane angle when the
	 * dip is resolved, strike difference otherwise); the angles to each
	 */
	matchedPlane?: 1 | 2
	angleToNp1?: number
	angleToNp2?: number
}

interface Sheet {
	node: PlaneSheetNode
	/** True-km center and in-plane axes (unit) with half extents */
	center: Vec
	along: Vec
	down: Vec
	halfAlong: number
	halfDown: number
	label: string
	text?: TextNode
}

export class FaultPlanesNode extends NodeBase {
	public readonly summary: FaultPlaneSummary = {}
	private readonly sheets: Sheet[] = []
	private readonly planeContext: StovokorContext
	private readonly layout: CutoutLayout
	private readonly labelSizeKm: number
	private font?: Font

	constructor(context: StovokorContext, layout: CutoutLayout, mainshock: { x: number; depthKm: number; z: number; mag: number }, fit: PlaneFit | undefined, tensor: MomentTensor | undefined, labelSizeKm: number) {
		super()
		this.planeContext = context
		this.layout = layout
		this.labelSizeKm = labelSizeKm
		const floor = layout.depthFloorKm

		if(fit) {
			this.summary.fit = { strikeDeg: fit.strikeDeg, dipDeg: fit.dipDeg, dipResolved: fit.dipResolved, trendResolved: fit.trendResolved, n: fit.n, flatness: fit.flatness }
			if(fit.dipResolved) {
				this.addSheet(fit.centroid, fit.strikeDir, fit.dipDir, fit.halfLength, fit.halfWidth, floor, cFitColor,
					`Aftershock plane ${pad3(fit.strikeDeg)}° / ${Math.round(fit.dipDeg)}°`)
			}
		}

		if(tensor) {
			this.summary.nodalPlanes = { np1: tensor.np1, np2: tensor.np2, source: tensor.source }
			// A round cloud with fixed depths has nothing to compare against
			if(fit && (fit.dipResolved || fit.trendResolved)) {
				if(fit.dipResolved) {
					const n1 = normalFromStrikeDip(tensor.np1.strikeDeg, tensor.np1.dipDeg, layout)
					const n2 = normalFromStrikeDip(tensor.np2.strikeDeg, tensor.np2.dipDeg, layout)
					this.summary.angleToNp1 = planeAngleDeg(fit.normal, n1)
					this.summary.angleToNp2 = planeAngleDeg(fit.normal, n2)
				} else {
					// Depths carry nothing; compare the trend of the cloud with each strike
					this.summary.angleToNp1 = strikeDifferenceDeg(fit.strikeDeg, tensor.np1.strikeDeg)
					this.summary.angleToNp2 = strikeDifferenceDeg(fit.strikeDeg, tensor.np2.strikeDeg)
				}
				const best = this.summary.angleToNp1 <= this.summary.angleToNp2 ? 1 : 2
				const bestAngle = Math.min(this.summary.angleToNp1, this.summary.angleToNp2)
				if(bestAngle <= cMatchAngleDeg) this.summary.matchedPlane = best
			}
			// Sheet size from the rupture scaling laws (Wells & Coppersmith): length
			// along strike, width down dip (log W = 0.32 M - 1.01)
			const halfL = Math.max(3, ruptureLengthKm(mainshock.mag) * 0.45)
			const halfW = Math.max(3, Math.pow(10, 0.32 * mainshock.mag - 1.01) * 0.5)
			const hypo = { x: mainshock.x, y: -mainshock.depthKm, z: mainshock.z }
			const colorFor = (which: 1 | 2): color => {
				if(!this.summary.matchedPlane) return cNeutralColor
				return this.summary.matchedPlane === which ? cMatchColor : cOtherColor
			}
			for(const [which, np] of [[1, tensor.np1], [2, tensor.np2]] as const) {
				const axes = nodalAxes(np, layout)
				this.addSheet(hypo, axes.along, axes.down, halfL, halfW, floor, colorFor(which),
					`NP${which} ${pad3(np.strikeDeg)}° / ${Math.round(np.dipDeg)}°`)
			}
		}
	}

	public get hasContent(): boolean {
		return this.sheets.length > 0
	}

	public set opacity(value: number) {
		for(const s of this.sheets) s.node.opacity = value
	}

	public setFont(font: Font): void {
		this.font = font
		for(const s of this.sheets) {
			if(s.text) continue
			s.text = new TextNode(this.planeContext, s.label, font, this.labelSizeKm * 0.75)
			s.text.color = rgba(0.90, 0.94, 1.0, 1)
			this.addNode(s.text)
		}
	}

	/**
	 * Labels lie in each sheet along its top edge, reading left to right
	 * and facing whichever side the camera is on
	 */
	public layoutLabels(cameraLocal: vec3): void {
		if(!this.font) return
		const scale = this.layout.depthScale
		for(const s of this.sheets) {
			if(!s.text) continue
			// Top edge center in frame coordinates
			const top = {
				x: s.center.x - s.down.x * s.halfDown,
				y: (s.center.y - s.down.y * s.halfDown) * scale,
				z: s.center.z - s.down.z * s.halfDown
			}
			// Camera side of the sheet
			const normal = cross(s.along, s.down)
			const toCam = { x: cameraLocal.x - top.x, y: cameraLocal.y - top.y, z: cameraLocal.z - top.z }
			const side = dot(normal, toCam) >= 0 ? 1 : -1
			const face = new vec3(normal.x * side, normal.y * side, normal.z * side)
			// Reading direction: along the sheet such that it runs to the viewer's right
			const up = normalize3(new vec3(-s.down.x, -s.down.y * scale, -s.down.z))
			let right = normalize3(cross3(up, face))
			if(right.length < 1e-6) right = new vec3(s.along.x, 0, s.along.z)
			const size = this.labelSizeKm * 0.75
			const at = new vec3(top.x + up.x * size * 0.8, top.y + up.y * size * 0.8, top.z + up.z * size * 0.8)
			// TextGeometry reads from its -z side with +y down (see QuakeCutoutNode.wallText)
			const tRight = right
			const tUp = up.multiply(-1)
			const tFace = face.multiply(-1)
			s.text.transform = new matrix([
				tRight.x, tRight.y, tRight.z, 0,
				tUp.x, tUp.y, tUp.z, 0,
				tFace.x, tFace.y, tFace.z, 0,
				at.x, at.y, at.z, 1
			])
		}
	}

	private addSheet(center: Vec, along: Vec, down: Vec, halfAlong: number, halfDown: number, floorKm: number, tint: color, label: string): void {
		// Keep the sheet between sea level and the box floor along its dip
		const sinDip = Math.max(Math.abs(down.y), 1e-3)
		const upRoom = -center.y / sinDip
		const downRoom = (floorKm + center.y) / sinDip
		const hd = Math.max(1, Math.min(halfDown, upRoom, downRoom))
		const node = new PlaneSheetNode(this.planeContext, center, along, down, halfAlong, hd, this.layout.depthScale, tint)
		node.renderOrder = 1
		this.addNode(node)
		this.sheets.push({ node, center, along, down, halfAlong, halfDown: hd, label })
	}
}

/** In-plane axes of a nodal plane in the block frame: along strike and down dip */
function nodalAxes(np: NodalPlane, layout: CutoutLayout): { along: Vec; down: Vec } {
	const rel = np.strikeDeg * Math.PI / 180 - layout.strike
	const dip = np.dipDeg * Math.PI / 180
	const along = { x: Math.cos(rel), y: 0, z: Math.sin(rel) }
	const dipAz = rel + Math.PI / 2
	const down = { x: Math.cos(dipAz) * Math.cos(dip), y: -Math.sin(dip), z: Math.sin(dipAz) * Math.cos(dip) }
	return { along, down }
}

export class PlaneSheetNode extends NodeBase {
	private readonly sheetRenderer: PlaneSheetRenderer

	constructor(context: StovokorContext, center: Vec, along: Vec, down: Vec, halfAlong: number, halfDown: number, depthScale: number, tint: color) {
		super()
		const geometry = new PlaneSheetGeometry(context, center, along, down, halfAlong, halfDown, depthScale)
		this.sheetRenderer = new PlaneSheetRenderer(context, geometry)
		this.sheetRenderer.color.value = tint
		this.renderer = this.sheetRenderer
	}

	public set opacity(value: number) {
		this.sheetRenderer.opacity.value = value
	}
}

class PlaneSheetRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public color = new UniformDynamic<color>(cNeutralColor)
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("cutout_plane"), geometry, [transparentMaterial])
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

class PlaneSheetGeometry implements Geometry {
	public position: VertexAttribute
	public uv: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, c: Vec, along: Vec, down: Vec, halfAlong: number, halfDown: number, depthScale: number) {
		this.position = new VertexAttribute(context, 3)
		this.uv = new VertexAttribute(context, 2)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.uv])

		const corners: Array<[number, number]> = [[-1, -1], [1, -1], [1, 1], [-1, 1]]
		for(const [a, d] of corners) {
			const x = c.x + along.x * a * halfAlong + down.x * d * halfDown
			const y = (c.y + along.y * a * halfAlong + down.y * d * halfDown) * depthScale
			const z = c.z + along.z * a * halfAlong + down.z * d * halfDown
			this.position.addPos(x, y, z)
			this.uv.add((a + 1) / 2, (d + 1) / 2)
		}
		this.indexBuffer.quad(0, 1, 2, 3)
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

function pad3(deg: number): string {
	return String(Math.round(((deg % 360) + 360) % 360)).padStart(3, "0")
}

function dot(a: Vec, b: Vec): number {
	return a.x * b.x + a.y * b.y + a.z * b.z
}

function cross(a: Vec, b: Vec): Vec {
	return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x }
}

function cross3(a: vec3, b: vec3): vec3 {
	return new vec3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x)
}

function normalize3(v: vec3): vec3 {
	const len = v.length
	return len > 1e-9 ? v.multiply(1 / len) : v
}
